import { Box, Button, Grid, PageTitle, Spacer, Text } from '@flexisaf/flexibull2';
import { useSelector } from 'react-redux';

import UpgradeIMG from 'assets/upgrade.svg';
import { Theme } from 'config/theme';
import useSubscriptionDetail from 'general-hooks/useSubscriptionDetail';
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { selectCurrentSubscription } from 'redux/configuration/selector';
import { select_school_admin, select_super_admin } from 'redux/privileges/selectors';
import styled from 'styled-components';
import { subscriptionFeatures } from 'utils/utils';

// Extended types to support dynamic content across different features
export type FeatureHighlight = {
  iconNode?: React.ReactNode;
  iconClass?: string;
  title: string;
  description: string;
};

type IProps = {
  featureName: string;
  featureDescription?: string;
  title?: string;
  subtitle?: string;
  headerImage?: string;
  videoThumbnail?: string;
  videoUrl?: string;
  videoCaption?: string;
  highlights?: FeatureHighlight[];
  requiredPlan?: string;
  unlockMessage?: string;
  footerHighlights?: string[];
  footerSubNotes?: string[];
};

const PageWrapper = styled(Box)`
  max-width: 1190px;
  width: 100%;
  margin: 0 auto;
  padding: 36px 20px 56px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const HeroSection = styled(Box)`
  width: 100%;
  max-width: 760px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const PageHeaderImage = styled.div`
  width: 120px;
  height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
`;

const ContentCard = styled(Box)`
  width: 100%;
  max-width: 600px;
  border-radius: 24px;
  box-shadow: 0px 0px 0px 1px rgba(0, 0, 0, 0.02), 0px 1px 2px 0px rgba(14, 18, 27, 0.12),
    0px 0px 0px 1px rgba(0, 0, 0, 0.04);
`;

const MediaCard = styled(Box)`
  width: 100%;
  min-height: 300px;
  padding: 4px;
  background: #383a3f;
  border-radius: 24px;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const VideoOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.24);
`;

const PlayButton = styled.button`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: none;
  background: ${Theme.PrimaryRed};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0px 16px 32px rgba(216, 30, 91, 0.18);
`;

const PlayTriangle = styled.span`
  width: 0;
  height: 0;
  border-left: 12px solid #ffffff;
  border-top: 8px solid transparent;
  border-bottom: 8px solid transparent;
  margin-left: 4px;
`;

const PlanContent = styled(Box)`
  width: 100%;
  background-color: '#FFFFFF';
  border-radius: 24px;
  padding: 28px;
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const PlanHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  text-align: left;
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(191, 216, 252, 0.5);
  color: ${Theme.PrimaryColor};
  border-radius: 7px;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 600;
`;

const ActionButton = styled(Button)`
  width: 100%;
  max-width: 100%;
  border-radius: 6px;
  padding: 12px 0;
  font-size: 15px;
  font-weight: 600;
`;

const BulletList = styled(Box)`
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
`;

const BulletItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  color: #5c5c5c;
  font-size: 12px;
  font-weight: 600;
  min-width: 220px;
`;

const FeatureGrid = styled(Grid)`
  width: 100%;
`;

const MiniFeatureCard = styled(Box)`
  background: ${Theme.PrimaryWhite};
  border-radius: 24px;
  padding: 28px;
  display: flex;
  gap: 16px;
  min-height: 124px;
  align-items: center;
`;

const MiniIconWrapper = styled.div`
  min-width: 44px;
  min-height: 44px;
  border-radius: 50%;
  background: ${Theme.PrimaryFadedBlue};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${Theme.PrimaryColor};
  font-size: 18px;
`;

const PlanDescription = styled(Text)`
  line-height: 1.6;
  color: #5c5c5c;
`;

const toTitleCase = (str: string) => {
  if (!str) return '';
  return str
    .toLowerCase()
    .split(/[\s_]+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const getYoutubeThumbnail = (url) => {
  if (!url) return null;

  // Regex to match various YouTube URL formats (youtube.com, youtu.be, embed)
  const regExp = /^.*(youtu\.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
  const match = url.match(regExp);

  if (match && match[2].length === 11) {
    // match[2] is the Video ID.
    // You can use 'hqdefault.jpg' (standard quality) or 'maxresdefault.jpg' (1080p, if available)
    return `https://img.youtube.com/vi/${match[2]}/hqdefault.jpg`;
  }

  return null;
};

export const HigherPlanRequired = ({
  featureName,
  featureDescription,
  title,
  subtitle,
  headerImage,
  videoUrl,
  videoThumbnail,
  videoCaption,
  highlights = [],
  requiredPlan = 'Premium',
  unlockMessage,
  footerHighlights = [
    'No setup required',
    'Your records stay organized',
    'Works seamlessly with existing data',
  ],
  footerSubNotes = [
    'No setup required',
    'Your records stay organized from day one',
    'Works seamlessly with your existing data',
  ],
}: IProps) => {
  const history = useNavigate();
  const currentSubscription = useSelector(selectCurrentSubscription);
  const superAdmin = useSelector(select_super_admin);
  const schoolAdmin = useSelector(select_school_admin);

  const displayTitle = title || `Upgrade to unlock ${featureName}`;
  const displaySubtitle =
    subtitle ||
    featureDescription ||
    'Contact your administrator to upgrade your plan to access this feature.';

  const displayThumbnail = videoThumbnail || getYoutubeThumbnail(videoUrl);

  const currentPlanLabel =
    currentSubscription?.subscription?.subscription_plan?.name ||
    toTitleCase(currentSubscription?.subscription?.subscription_plan?.id || 'standard');

  const planText =
    unlockMessage ||
    `${
      featureName || 'This feature'
    } is available on the ${requiredPlan} plan. Upgrade to unlock this feature along with other advanced capabilities.`;

  return (
    <>
      <PageTitle>
        <Text bold size={Theme.H6}>
          {' '}
          Pricing & Subscription
        </Text>
      </PageTitle>
      <PageWrapper pad="0" align="center">
        <HeroSection>
          <PageHeaderImage>
            <img
              src={headerImage || UpgradeIMG}
              alt="Upgrade Icon"
              style={{ maxWidth: '100%', maxHeight: '100%' }}
            />
          </PageHeaderImage>

          <Spacer space={24} />
          <Text block size={Theme.H3} style={{ fontWeight: '700', color: Theme.PrimaryFontColor }}>
            {displayTitle}
          </Text>
          <Spacer space={12} />
          <Text
            block
            size={Theme.H6}
            color={Theme.PrimaryGrey}
            style={{ maxWidth: '640px', margin: '0 auto', lineHeight: '1.75' }}
          >
            {displaySubtitle}
          </Text>
        </HeroSection>

        <Spacer space={40} />

        <ContentCard>
          <Box
            pad="0px"
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '4px',
              background: '#F7F7F7',
              borderRadius: '24px',
              padding: '4px',
            }}
          >
            <MediaCard>
              {videoThumbnail ? (
                <img
                  src={displayThumbnail as string}
                  alt={videoCaption || 'Video thumbnail'}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              ) : null}
              <VideoOverlay />
              {videoUrl ? (
                <a href={videoUrl} target="_blank" rel="noreferrer" style={{ zIndex: 1 }}>
                  <PlayButton type="button">
                    <PlayTriangle />
                  </PlayButton>
                </a>
              ) : (
                <PlayButton type="button" style={{ zIndex: 1 }}>
                  <PlayTriangle />
                </PlayButton>
              )}
            </MediaCard>

            <PlanContent style={{ background: '#FFFFFF' }}>
              <PlanHeader>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Text
                      block
                      size={Theme.H6}
                      style={{ fontWeight: '700', color: Theme.PrimaryFontColor }}
                    >
                      {currentPlanLabel} Plan
                    </Text>
                    <Badge>Current plan</Badge>
                  </div>
                  <PlanDescription size={Theme.Paragraph}>{planText}</PlanDescription>
                </div>
              </PlanHeader>

              <ActionButton
                color={Theme.PrimaryColor}
                onClick={() => history('/select-plan')}
                style={{ color: Theme.PrimaryWhite }}
              >
                Upgrade now
              </ActionButton>

              <BulletList>
                {footerHighlights.map((item, index) => (
                  <BulletItem key={index}>
                    <span
                      className="saf-shield-tick"
                      style={{ color: Theme.PrimaryColor, fontSize: '14px' }}
                    />
                    {item}
                  </BulletItem>
                ))}
              </BulletList>
            </PlanContent>

            <FeatureGrid default="repeat(2, minmax(0, 1fr))" gap="4px">
              {highlights.map((item, index) => (
                <MiniFeatureCard key={index}>
                  <MiniIconWrapper>
                    {item.iconNode ? (
                      item.iconNode
                    ) : (
                      <span className={item.iconClass || 'saf-star'} />
                    )}
                  </MiniIconWrapper>
                  <div style={{ textAlign: 'left' }}>
                    <Text
                      block
                      size="14px"
                      style={{ fontWeight: '500', color: Theme.PrimaryFontColor }}
                    >
                      {item.title}
                    </Text>
                    <Text
                      block
                      size={Theme.Paragraph}
                      style={{ color: '#5c5c5c', marginTop: '4px' }}
                    >
                      {item.description}
                    </Text>
                  </div>
                </MiniFeatureCard>
              ))}
            </FeatureGrid>
          </Box>
        </ContentCard>
      </PageWrapper>
    </>
  );
};

export const SubscriptionCheckWrapper = (props: any) => {
  const { children, code } = props;
  const { checkSubscriptionPermission } = useSubscriptionDetail();
  const access = checkSubscriptionPermission(code);
  if (access === undefined) {
    return null;
  }
  if (access) {
    return children;
  } else {
    const featureData = subscriptionFeatures[code] || {};
    return (
      <HigherPlanRequired
        featureName={featureData?.name}
        featureDescription={featureData?.description}
        title={featureData?.title}
        subtitle={featureData?.subtitle}
        headerImage={featureData?.headerImage}
        videoThumbnail={featureData?.videoThumbnail}
        videoUrl={featureData?.videoUrl}
        videoCaption={featureData?.videoCaption}
        highlights={featureData?.highlights}
        requiredPlan={featureData?.requiredPlan}
        unlockMessage={featureData?.unlockMessage}
        footerHighlights={featureData?.footerHighlights}
        footerSubNotes={featureData?.footerSubNotes}
      />
    );
  }
};

const isOpenRoute = [
  '/select-plan',
  '/plans',
  '/subscription',
  '/user-profile',
  '/expired-plan',
  '/configurations',
  '/sessions-and-terms',
];

export const SubscriptionCheckRedirection = () => {
  const history = useNavigate();
  const currentPathname = window?.location?.pathname;

  const { currentSubscription, subscriptionDetail, isSubscriptionUser } = useSubscriptionDetail();

  useEffect(() => {
    if (isSubscriptionUser && currentSubscription?.id) {
      if (!subscriptionDetail?.isSubscriptionActive) {
        if (!isOpenRoute.includes(currentPathname)) {
          history('/expired-plan');
        }
      }
    }
  }, [
    currentPathname,
    currentSubscription?.id,
    history,
    isSubscriptionUser,
    subscriptionDetail?.isSubscriptionActive,
  ]);
  return <></>;
};
